'use client'

import { useSidebarStore } from '@/app/hooks/sidebarStore'
import { useRouter } from 'next/navigation'
import useLoginModal from '../../hooks/useLoginModal'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { usePathname } from 'next/navigation'
import { useNotification } from "@/app/providers/WebSocketProvider"
import {
  Menu, X, Home, Calendar, Settings,
  ChevronLeft, Heart, Mic, MessageSquare, Ticket, ShoppingCart
} from 'lucide-react'

interface SidebarProps {
  userId?: string | null;
}

interface SidebarLinkProps {
  href: string
  icon: React.ReactNode
  label: string
  pathname: string
  small?: boolean
  badge?: number
  userId?: string | null
  onClick?: () => void
}

// 1. SidebarLink movido para fora do componente pai para otimização de performance
function SidebarLink({
  href,
  icon,
  label,
  pathname,
  small = false,
  badge = 0,
  userId,
  onClick
}: SidebarLinkProps) {
  const isActive = pathname === href
  const isProtectedRoute = href.startsWith('/dashboard') || href.startsWith('/my') || href.startsWith('/contracts') || href.startsWith('/inbox')

  const router = useRouter()
  const loginModal = useLoginModal()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    if (onClick) onClick() // Executa callback (ex: resetUnreadCount)

    if (isProtectedRoute && !userId) {
      loginModal.open()
    } else {
      router.push(href)
    }
  }

  return (
    <a
      href={href}
      onClick={handleClick}
      className={cn(
        'relative flex items-center transition-colors px-3 py-2 rounded-md',
        small ? 'justify-center' : 'gap-2 justify-between',
        isActive ? 'text-green-800 font-semibold bg-green-50/50' : 'hover:text-black text-gray-700 hover:bg-gray-100',
        small ? 'text-sm' : 'text-base'
      )}
    >
      <div className="flex items-center gap-2 relative">
        <span className="relative">
          {icon}
          {/* Badge em modo recolhido (small): aponta uma bolinha vermelha no próprio ícone */}
          {small && badge > 0 && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
          )}
        </span>

        {!small && <span className="whitespace-nowrap">{label}</span>}
      </div>

      {/* Badge em modo expandido: exibe o número de mensagens */}
      {!small && badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </a>
  )
}

const SidebarClient: React.FC<SidebarProps> = ({ userId }) => {
  const { isCollapsed, toggleCollapse } = useSidebarStore()
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()
  const { unreadCount, resetUnreadCount } = useNotification()

  // Fecha a sidebar mobile ao mudar de rota
  useEffect(() => {
    if (isOpen) setIsOpen(false)
  }, [pathname])

  return (
    <>
      {/* Toggle mobile */}
      <button
        className="md:hidden fixed top-[128px] left-4 z-50 text-gray-800 p-2 bg-white rounded-md shadow-sm border border-gray-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar Desktop */}
      <aside className={`md:block hidden bg-white border border-gray-200
        shadow-md text-gray-500 z-40 rounded-xl fixed left-0 top-[128px]
        h-[calc(100vh-128px)] transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-16' : 'w-64'}`}>
        
        <div className="flex justify-end p-2">
          <button onClick={toggleCollapse} className="p-1 hover:bg-gray-100 rounded-lg">
            {isCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <nav className={cn(
          'p-3 space-y-2 text-sm text-gray-700',
          isCollapsed && 'flex flex-col items-center px-1'
        )}>
          {!isCollapsed && (
            <div className="p-3 text-base text-center font-semibold border-b border-gray-200 text-gray-800">
              Menu
            </div>
          )}

          <SidebarLink href="/dashboard" icon={<Home size={18} />} label="Dashboard" pathname={pathname} small={isCollapsed} userId={userId} />
          <SidebarLink href="/myproducts" icon={<Mic size={18} />} label="Meus Shows" pathname={pathname} small={isCollapsed} userId={userId} />
          <SidebarLink href="/myfavorites" icon={<Heart size={18} />} label="Meus Favoritos" pathname={pathname} small={isCollapsed} userId={userId} />
          <SidebarLink href="/myreservations" icon={<Calendar size={18} />} label="Minhas Reservas" pathname={pathname} small={isCollapsed} userId={userId} />
          
          <SidebarLink
            href="/inbox"
            icon={<MessageSquare size={18} />}
            label="Mensagens"
            pathname={pathname}
            small={isCollapsed}
            badge={unreadCount}
            userId={userId}
            onClick={resetUnreadCount}
          />

          <SidebarLink href="/mytickets" icon={<Ticket size={18} />} label="Meus Tickets" pathname={pathname} small={isCollapsed} userId={userId} />
          <SidebarLink href="/shop" icon={<ShoppingCart size={18} />} label="Shop" pathname={pathname} small={isCollapsed} userId={userId} />
          <SidebarLink href="/settings" icon={<Settings size={18} />} label="Configurações" pathname={pathname} small={isCollapsed} userId={userId} />
        </nav>
      </aside>

      {/* Sidebar Mobile */}
      <aside
        className={`md:hidden fixed top-[128px] left-0 h-[calc(100vh-128px)] w-64 bg-white border border-gray-200 shadow-xl text-black z-40 rounded-r-xl transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-4 text-lg text-center text-gray-700 font-semibold border-b border-gray-100">
          Menu
        </div>

        <nav className="p-4 space-y-2 text-sm">
          <SidebarLink href="/dashboard" icon={<Home size={18} />} label="Dashboard" pathname={pathname} userId={userId} />
          <SidebarLink href="/myproducts" icon={<Mic size={18} />} label="Meus Shows" pathname={pathname} userId={userId} />
          
          <SidebarLink
            href="/inbox"
            icon={<MessageSquare size={18} />}
            label="Mensagens"
            pathname={pathname}
            badge={unreadCount}
            userId={userId}
            onClick={resetUnreadCount}
          />

          <SidebarLink href="/myreservations" icon={<Calendar size={18} />} label="Minhas Reservas" pathname={pathname} userId={userId} />
          <SidebarLink href="/mytickets" icon={<Ticket size={18} />} label="Meus Tickets" pathname={pathname} userId={userId} />
          <SidebarLink href="/myfavorites" icon={<Heart size={18} />} label="Meus Favoritos" pathname={pathname} userId={userId} />
          <SidebarLink href="/shop" icon={<ShoppingCart size={18} />} label="Shop" pathname={pathname} userId={userId} />
          <SidebarLink href="/settings" icon={<Settings size={18} />} label="Configurações" pathname={pathname} userId={userId} />
        </nav>
      </aside>

      {/* Overlay escuro para fechar ao clicar fora no mobile */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 z-30 top-[128px]" 
          onClick={() => setIsOpen(false)} 
        />
      )}
    </>
  )
}

export default SidebarClient
